import Vue from 'vue';
import Router, { NavigationGuard } from 'vue-router';
import user from '@/api/user';
import store from '@/store';
import nprogress from 'nprogress';
import { config, setConfig } from 'CONFIG';
import { headerHandle } from '@/common/js/header';
import reolinkGa from '@/common/js/gtagPageview';

import { httpManager as http } from '@/plugins/HttpManager';
import { router } from 'CONFIG/router';
import bcsdk from 'Bcsdk';
import { AxiosError } from 'axios';
import { uidHandler } from '@/lib';
import { getShowAiInfo, queryStorageApi } from '@/utils/business';
import i18n, { LANG_KEY } from '@/lang/reolink';
import { getCookie } from '@/common/js/utils';

let hasFetchedProfile = false;
Vue.use( Router );
const handleQueryParams: NavigationGuard = ( to, from, next ) => {

    const id = to.query.uid ?? to.query.s_id ?? to.query.s_code;
    const inDoorbellProgressUid = to.query.inDoorbellProgressUid;
    const shouldUpdateYmh = typeof id === 'string' && typeof to.query.ymh !== 'string';
    const shouldUpdateInDoorbellProgress = typeof inDoorbellProgressUid === 'string' && to.query.inDoorbellProgress === undefined;
    const shouldUpdatePromotionType = typeof from.query.promotionType === 'string' && to.query.promotionType === undefined && ['regress', 'cloud_exposure_one', 'cloud_exposure_seven'].includes( from.query.promotionType );

    const updatedQuery = { ...to.query };
    if ( shouldUpdateYmh ) {

        updatedQuery.ymh = uidHandler.encrypt( id );
        delete updatedQuery.uid;
        delete updatedQuery.s_id;
        delete updatedQuery.s_code;

    }
    if ( shouldUpdateInDoorbellProgress ) {

        updatedQuery.inDoorbellProgress = uidHandler.encrypt( inDoorbellProgressUid );
        delete updatedQuery.inDoorbellProgressUid;

    }
    if ( shouldUpdatePromotionType ) {

        updatedQuery.promotionType = from.query.promotionType;

    } if ( shouldUpdateInDoorbellProgress && !to.path.includes( 'dashboard' )) {

        next({
            path: to.path,
            query: { inDoorbellProgress: from.query.inDoorbellProgress, ...updatedQuery },
            replace: true
        });

    } else if ( shouldUpdateYmh || shouldUpdateInDoorbellProgress || shouldUpdatePromotionType ) {

        next({ path: to.path, query: updatedQuery, replace: true });

    } else {

        next();

    }

};

const rawAiFeatures = [...config.aiFeatures];
const handleShowAiInfo = async (): Promise<void> => {

    try {

        const showAiInfo = await getShowAiInfo( store.getters.country, rawAiFeatures.join( ',' ));
        setConfig( 'aiFeatures', showAiInfo ? rawAiFeatures : [] );

    } catch ( e ) {

        console.error( 'Error fetching AI feature info:', e );

    }

};

const handle = async ( requiresAuth: boolean, targetUrl?: string ): Promise<boolean|undefined> => {

    nprogress.set( 0.3 );
    try {

        let logined;
        if ( config.platform === 'web' ) {

            logined = await user.login( requiresAuth, targetUrl );

        } else {

            logined = user.appIsLogin;

        }
        store.commit( 'PAGESKIP' );
        await store.dispatch( 'closeTopInfo' );
        // 在web页面时，每次跳转都请求用户信息
        // 在webview时，只请求一次用户信息
        if ( logined ) {

            if ( config.platform === 'web' ) {

                await user.getUserInfo();
                await handleShowAiInfo();

            } else if ( !hasFetchedProfile ) {

                await user.getUserInfo();
                await handleShowAiInfo();
                hasFetchedProfile = true;

            }
            if ( !getCookie( LANG_KEY )) {

                i18n.locale = config.countryData.countries.find( item => item.countryCode === store.getters.country )?.langCode ?? config.lang;

            }
            store.commit( 'STATUS_CHANGE', 'logined' );
            return true;

        } else {

            store.commit( 'STATUS_CHANGE', 'notLogined' );
            if ( !getCookie( LANG_KEY )) {

                i18n.locale = config.countryData.countries.find( item => item.countryCode === store.getters.country )?.langCode ?? config.lang;

            }
            return !requiresAuth;

        }

    } catch ( err ) {

        const symbol = ( err as AxiosError ).response?.data.error.symbol;

        if ( symbol === 'account_forbidden' ) {

            await bcsdk.clearAccountCookie();
            user.clearToken();
            store.commit( 'LOGOUT' );
            store.commit( 'STATUS_CHANGE', 'notLogined' );
            http.setHeader( `Authorization`, `` );

        }
        await user.goToAccountCenter( requiresAuth, targetUrl, '' );

    }

};

router.beforeEach( async ( to, from, next ) => {

    nprogress.start();
    const requiresAuth = to.meta?.requiresAuth;

    // 将当前路由的认证状态存储到 window 对象，供 HTTP 拦截器使用
    ( window as any ).__CURRENT_ROUTE_REQUIRES_AUTH__ = requiresAuth;

    for ( const key of queryStorageApi.keys ) {

        const queryKey = key.replace( 'query_', '' );

        if ( to.query[queryKey] ) {

            queryStorageApi.setItem( key, String( to.query[queryKey] ));

        }

    }

    if ( requiresAuth ) {

        const suc = await handle( requiresAuth, to.fullPath );
        if ( suc ) {

            headerHandle( to );
            window.scrollTo( 0, 0 );
            handleQueryParams( to, from, next );

        }

    } else {

        headerHandle( to );
        window.scrollTo( 0, 0 );
        /**
         * 如果已登录获取完登录信息再加入路由
         */
        if ( user.accuontCenterIsLogined() || user.appIsLogin ) {

            try {

                await handle( requiresAuth, to.fullPath );

            } catch ( e ) {

                console.error( e );

            } finally {

                handleQueryParams( to, from, next );

            }

        } else {

            await handle( requiresAuth );
            handleQueryParams( to, from, next );

        }

    }
    // play页面可直接切换视频id进行切换
    if ( to.path.includes( 'play' ) || to.path.includes( 'terms-of-use' ) || to.name === 'introduction' ) {

        nprogress.done();

    }

});

router.afterEach( to => {

    reolinkGa.updateLocation( to.name!, to.path );
    reolinkGa.pageview();
    $( $( 'html' )[0] ).attr( 'lang', `${store.state.lang.lang}` );

    // 为标记 noIndex 的页面添加 meta robots 标签
    if ( to.meta?.noIndex ) {

        // 移除已存在的 robots meta 标签
        const existingRobotsTag = document.querySelector( 'meta[name="robots"]' );
        if ( existingRobotsTag ) {

            existingRobotsTag.remove();

        }

        // 添加新的 robots meta 标签
        const robotsTag = document.createElement( 'meta' );
        robotsTag.name = 'robots';
        robotsTag.content = 'noindex, nofollow, noarchive, nosnippet, noimageindex';
        document.head.appendChild( robotsTag );

    } else {

        // 如果不是 noIndex 页面，移除 robots meta 标签（如果存在）
        const existingRobotsTag = document.querySelector( 'meta[name="robots"]' );
        if ( existingRobotsTag ) {

            existingRobotsTag.remove();

        }

    }

});

export default router;
